import {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {loadStripe} from '../utils/stripe';

/**
 * Stripe Payment Element for a server-created PaymentIntent. After Stripe confirms the
 * payment, `onPaid` runs (it should call the aidb confirm endpoint, which re-checks the
 * intent server-side; the browser's word is never trusted).
 */
export default function StripeCheckout({clientSecret, publishableKey, amountCents, onPaid}) {
  const mountRef = useRef(null);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // onPaid can unmount this form (the payment succeeded and the page moved on): no late updates.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadStripe(publishableKey)
      .then((s) => {
        if (cancelled) return;
        const els = s.elements({clientSecret});
        els.create('payment').mount(mountRef.current);
        setStripe(s);
        setElements(els);
      })
      .catch((err) => setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [clientSecret, publishableKey]);

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const result = await stripe.confirmPayment({elements, redirect: 'if_required'});
    if (result.error) {
      setError(result.error.message);
      setBusy(false);
      return;
    }
    try {
      await onPaid();
    } catch (err) {
      setError(err.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <form onSubmit={pay} className="stripe-checkout" aria-label="Payment">
      <div ref={mountRef} data-testid="payment-element" />
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary w-100 mt-3" disabled={!stripe || busy}>
        {busy ? 'Processing…' : `Pay $${(amountCents / 100).toFixed(2)}`}
      </button>
    </form>
  );
}

StripeCheckout.propTypes = {
  clientSecret: PropTypes.string.isRequired,
  publishableKey: PropTypes.string.isRequired,
  amountCents: PropTypes.number.isRequired,
  onPaid: PropTypes.func.isRequired,
};
